import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import LoadTracker from '../../services/LoadTracker';
import marketplaceSettingsService from '../../services/MarketplaceSettingsService';
import navigationService from '../../services/NavigationService';
import QuestionDisplayContainer from '../question/QuestionDisplay';
import stimulusHighlightsService from '../../services/StimulusHighlightsService';
import tocReader from '../../store/readHelpers/TocReader';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import scrollingService from '../../services/ScrollingService';
import objectTypes from '../../constants/ObjectTypes';
import UIConstants from '../../constants/UIConstants';
import uiService from '../../services/UIService';
import questionSelectionService from '../../services/QuestionSelectionService';

export const defaultState = { allQuestionsRendered: false, renderedQuestions: {}, scrolled: false, scrolling: false };
let scrollTimeout;

export class SectionQuestionsDisplay extends React.Component {
    static propTypes = {
        currentlyShown: PropTypes.object.isRequired,
        isQuestionRubricBlockShown: PropTypes.bool.isRequired,
        onReady: PropTypes.func.isRequired,
        section: PropTypes.object,
        stimulusHighlights: PropTypes.array,
    };

    constructor(props) {
        super(props);
        this.state = defaultState;
        this.isComponentMounted = false;
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.renderedQuestions = {};
        this.initLoadTracker(this.props.section);
    }

    componentDidMount() {
        this.waitingForSection = this.props.currentlyShown.sectionId;
        this.isComponentMounted = true;
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.currentlyShown.sectionId !== this.props.currentlyShown.sectionId) {
            this.initLoadTracker(newProps.section);
            this.renderedQuestions = {};
            this.setState(defaultState);
        } else if (
            newProps.currentlyShown.questionId !== this.props.currentlyShown.questionId ||
            (this.props.currentlyShown.scorableItem && newProps.currentlyShown.scorableItem !== this.props.currentlyShown.scorableItem)
        ) {
            this.setState({ scrolled: false, scrolling: false });
        }
    }

    componentDidUpdate(prevProps) {
        let isNewSection = prevProps.currentlyShown.sectionId !== this.props.currentlyShown.sectionId;

        if (!this.isThisQuestionAndAllPreviousQuestionsRendered(this.props.currentlyShown.questionId)) {
            if (isNewSection) {
                this.waitingForSection = this.props.currentlyShown.sectionId;
            }
            return;
        }

        if (this.waitingForSection === this.props.currentlyShown.sectionId) {
            this.waitingForSection = null;
            isNewSection = true;
        }

        if (!this.state.scrolling && !this.state.scrolled) {
            clearTimeout(scrollTimeout);
            this.setState({ scrolling: true }); // eslint-disable-line
            this.activateAndScrollToQuestion(
                this.props.currentlyShown.questionId,
                10,
                isNewSection
            ).then(() => {
                if (this.isComponentMounted) {
                    this.setState({ scrolled: true, scrolling: false }); // eslint-disable-line
                }
            });
        }
    }

    componentWillUnmount() {
        clearTimeout(scrollTimeout);
        this.isComponentMounted = false;
    }

    initLoadTracker = (section) => {
        const { onReady } = this.props;
        this.questionTrackers = {};
        this.loadTracker = new LoadTracker((success) => {
            if (typeof onReady === 'function') {
                onReady(success);
            }
        }, `SectionQuestionsDisplay - ${section.id}`);

        section.questions.forEach(({ contentRevisionId }) => {
            this.questionTrackers[contentRevisionId] = this.loadTracker.addItemToTrack(`Question - ${contentRevisionId}`);
        });

        this.loadTracker.setAllItemsAdded();
    };

    activateAndScrollToQuestion = (contentRevisionId, remainingRetries = 10, isNewSection = false, skipScroll = false) => {
        if (remainingRetries <= 0 || !this.isComponentMounted) {
            return Promise.reject();
        }
        const { currentlyShown, section, stimulusHighlights } = this.props;
        const shouldSkipScroll = skipScroll || currentlyShown.route.indexOf('skipScroll') > -1;
        let orderNumber = parseInt(currentlyShown.scorableItem, 10);
        if (scorableItemNumberingService.useNumberPerInteraction()
            && !scorableItemNumberingService.isValidOrderNumber(orderNumber, contentRevisionId)) {
            orderNumber = scorableItemNumberingService.getFirstValidOrderNumberInQuestion(contentRevisionId);
        }

        if (scorableItemNumberingService.useNumberPerInteraction()) {
            navigationService.navigateToScorableItem(section.id, contentRevisionId, orderNumber, shouldSkipScroll);
        } else {
            navigationService.navigateToQuestion(contentRevisionId, section.id, shouldSkipScroll);
        }

        const questionComponent = this.renderedQuestions[contentRevisionId];

        if (
            section.questions
                .find((question) => {
                    return question.category === objectTypes.CATEGORY.STIMULUS && question.contentRevisionId === contentRevisionId;
                })
        ) {
            const questionPortal = document.getElementById(`questionPortal-${contentRevisionId}`);
            const duration = isNewSection ? 0 : 500;
            if (questionPortal) {
                return scrollingService.scrollToElement(questionPortal, duration);
            }
        }

        if (questionComponent === undefined) {
            return Promise.resolve(); // This probably means we have navigated to a new section, so just forget about it.
        }

        const question = questionComponent.props.question;
        const sectionId = tocReader.getSectionIdForQuestionId(contentRevisionId);
        const questionSection = assessmentReader.getSectionById(sectionId);
        const stimulusPosition = assessmentReader.getSectionStimulusPosition(questionSection);
        const useFixedTopStimulus = marketplaceSettingsService.useFixedTopStimulus();
        const isFirstQuestion = this.isFirstQuestion(contentRevisionId);
        const isLastQuestion = this.isLastQuestion(contentRevisionId);
        const wait = isNewSection && isLastQuestion ? 300 : 10;
        let animationLength = 500;

        if (isNewSection && (isFirstQuestion || isLastQuestion)) {
            animationLength = 0;
        }
        stimulusHighlightsService.goToHighlight(question, stimulusHighlights.indexOf(question.contentItemId) !== -1);

        if (shouldSkipScroll) {
            return Promise.resolve();
        }

        if (isNewSection && stimulusPosition === 'top' && !useFixedTopStimulus) {
            return navigationService.scrollToTopStimulus(sectionId, wait, animationLength)
                .catch(() => {
                    return new Promise((resolve) => {
                        scrollTimeout = setTimeout(() => {
                            resolve(
                                this.activateAndScrollToQuestion(contentRevisionId, remainingRetries - 1, isNewSection, skipScroll)
                            );
                        }, 300);
                    });
                });
        }

        return navigationService.scrollToQuestion(sectionId, contentRevisionId, orderNumber, wait, animationLength)
            .catch(() => {
                return new Promise((resolve) => {
                    scrollTimeout = setTimeout(() => {
                        resolve(
                            this.activateAndScrollToQuestion(contentRevisionId, remainingRetries - 1, isNewSection, skipScroll)
                        );
                    }, 300);
                });
            });
    };

    isLastQuestion(contentRevisionId) {
        const { questions } = this.props.section;
        return questions[questions.length - 1].contentRevisionId === contentRevisionId;
    }

    isFirstQuestion(contentRevisionId) {
        const { questions } = this.props.section;
        return questions[0].contentRevisionId === contentRevisionId;
    }

    isThisQuestionAndAllPreviousQuestionsRendered(contentRevisionId) {
        if (questionSelectionService.isCandidateSelectedContentBlockEnabled()) return false;
        const { questions } = this.props.section;
        const questionList = questions.filter(item => item.category !== objectTypes.CATEGORY.STIMULUS);
        if (questionList.length === Object.keys(this.renderedQuestions).length) {
            return true;
        }

        let ok = true;
        let i = 0;
        let done = false;
        while (ok && !done && i < questionList.length) {
            ok = !!this.renderedQuestions[questionList[i].contentRevisionId];
            done = questionList[i].contentRevisionId === contentRevisionId;
            i++;
        }

        return ok;
    }

    onQuestionReady = (success, questionComponent) => {
        if (this.questionTrackers[questionComponent.props.question.contentRevisionId]) {
            this.questionTrackers[questionComponent.props.question.contentRevisionId](success);
            this.renderedQuestions[questionComponent.props.question.contentRevisionId] = questionComponent;
            this.setState({ renderedQuestions: this.renderedQuestions });
        }
    };

    isNestedSectionChanging = (currentQuestion, nextQuestion) => {
        return (currentQuestion?.nestedSectionId
            && currentQuestion.nestedSectionId !== nextQuestion?.nestedSectionId)
            || false;
    }

    render() {
        const { section, isQuestionRubricBlockShown } = this.props;
        const currentQuestion = assessmentReader.getCurrentQuestion();
        return (
            section ?
                <section id={`section-${section.id}`}>
                    {uiService.isLeftSectionLayout(section) && <div id={UIConstants.GAP_MATCH_SELECTOR_ID} />}
                    {
                        section.questions
                            .filter((question) => {
                                if (questionSelectionService.isCandidateSelectedContentBlockEnabled()) {
                                    return currentQuestion.nestedSectionId === question?.nestedSectionId;
                                }
                                return true;
                            })
                            .map((question, index, questions) => {
                                if (question.category === objectTypes.CATEGORY.STIMULUS) return null;

                                return (
                                    <QuestionDisplayContainer
                                        key={question.contentRevisionId}
                                        question={question}
                                        isLastQuestionInCurrentNestedSection={this.isNestedSectionChanging(
                                            question,
                                            questions?.[index + 1]
                                        )}
                                        focusOnLoad={index === 0}
                                        onReady={this.onQuestionReady}
                                        activateAndScrollToQuestion={this.activateAndScrollToQuestion}
                                        isQuestionRubricBlockShown={isQuestionRubricBlockShown}
                                    />
                                );
                            })
                    }
                </section>
                : null
        );
    }
}

function mapStateToProps(state) {
    const { assessment, itemFlag: { flaggedItems }, stimulusHighlights, currentlyShown, response, settings } = state;
    const section = assessment.sections.find((currentSection) => {
        return currentSection.id === parseInt(currentlyShown.sectionId, 10);
    });

    return {
        flaggedItems,
        stimulusHighlights,
        section,
        settingValues: settings.values,
        response,
        currentlyShown,
    };
}

const SectionQuestionsDisplayContainer = connect(mapStateToProps)(SectionQuestionsDisplay);
export default SectionQuestionsDisplayContainer;
